import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';

import HorizontalStepper from '@/components/HorizontalStepper';

import { CreateItemConfigStep } from './defaults';
import { CreateOrganizationItemFormData } from './types';

const CREATE_BUTTON_KEY = 'create';
const ACTIVATE_BUTTON_KEY = 'activate';

interface CreateOrganizationItemModalContentProps {
  handleSaveDraft?: () => void;
  isLoading?: boolean;
  draftExists?: boolean;
  steps?: CreateItemConfigStep[];
  draftAllowed?: boolean;
  title?: string;
}

interface ValidationError {
  path: string;
  type: string;
  message: string;
}

const CreateOrganizationItemModalContent: FC<CreateOrganizationItemModalContentProps> = ({
  handleSaveDraft,
  isLoading,
  draftExists,
  steps,
  draftAllowed,
  title
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { t } = useTranslation(['organizations', 'common', 'validation']);
  const [currentTabErrors, setCurrentTabErrors] = useState<ValidationError[] | null>(null);
  const { watch, getValues } = useFormContext<CreateOrganizationItemFormData>();
  const organizationName = watch('name');
  const maxStep = (steps?.length ?? 0) - 1;

  const handleNext = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (currentStep < maxStep) {
        e.preventDefault();
        setCurrentStep(prev => prev + 1);
      }
    },
    [currentStep, maxStep]
  );

  const handleBack = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  const Content = useMemo(() => steps?.[currentStep]?.content, [steps, currentStep]);

  const schema = useMemo(() => steps?.[currentStep]?.schema, [steps, currentStep]);

  useEffect(() => {
    const validate = async (values: FieldValues) => {
      try {
        await schema?.validate(values, {
          abortEarly: false
        });

        setCurrentTabErrors(null);
      } catch (errors) {
        const errorList = (errors as { inner: { path: string; type: string; message: string }[] })?.inner;
        setCurrentTabErrors(errorList);
      }
    };

    const values = getValues();
    const subscription = watch(validate);

    if (values) {
      validate(values);
    }

    return () => subscription?.unsubscribe?.();
  }, [schema, watch, getValues]);

  const stepLabels = useMemo(() => steps?.map(item => item.title!) ?? [], [steps]);

  const saveBtnKey = draftExists ? ACTIVATE_BUTTON_KEY : CREATE_BUTTON_KEY;

  const hasMultipleSteps = (steps?.length ?? 0) > 1;

  return (
    <Box sx={{ width: '100%', pt: hasMultipleSteps ? 0 : 2 }}>
      {hasMultipleSteps && (
        <Box sx={{ m: 4, mb: 2 }}>
          <HorizontalStepper labels={stepLabels} value={currentStep} />
        </Box>
      )}

      {Content && <Content />}

      <Stack
        direction="row"
        justifyContent="space-between"
        width="100%"
        sx={{
          paddingInlineStart: 2
        }}
      >
        {currentStep > 0 && (
          <Button onClick={handleBack} variant="text">
            {t('common:actions.back')}
          </Button>
        )}

        <Stack direction="row" gap={2} p={3} justifyContent="end" flexGrow={1}>
          {draftAllowed && (
            <Button onClick={handleSaveDraft} variant="text" disabled={!organizationName}>
              {t('layouts.organizations.create.saveDraft')}
            </Button>
          )}
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={isLoading || !!currentTabErrors}
            type={currentStep === maxStep ? 'submit' : 'button'}
          >
            {currentStep === maxStep
              ? [t(`layouts.organizations.create.${saveBtnKey}`), title].join(' ')
              : t('common:actions.next')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CreateOrganizationItemModalContent;
